import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { APIData, DSSetup, RecordData, Service, Timeframe } from 'webapp-types';
import {
	BlockVisualization,
	BlockVisualizationSpecial,
	DataSelectType,
	ForecastMethod,
	ForecastStatusType,
	ModelName,
	TimeframeType,
	type ForecastComparisonPeriod,
	type TimeUnit,
} from 'webapp/enums/enums';
import type { TrackingEvent } from 'webapp/enums/tracking';
import { serializeMetricSetupData } from 'webapp/utils/common';
import { getRelativeTime } from 'webapp/utils/number';

import type Block from 'webapp/models-custom/block';
import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import type DataSelect from 'webapp/models-custom/data-select';
import DateRange from 'webapp/models-custom/date-range';
import MetricSetup from 'webapp/models-custom/metric-setup';
import RegressorMetric, { type RegressorMetricData } from 'webapp/models-custom/regressor-metric';
import type VisualizationLineChart from 'webapp/models-custom/visualization/line-chart';
import type ForecastRecord from 'webapp/models/forecast';
import type SampleRecord from 'webapp/models/sample';

export interface ForecastMethodology {
	type: ForecastMethod;
	extra: {
		changeValue?: number;
		changePattern?: string;
	} | null;
}

export default class Forecast extends BufferedModel<
	ForecastRecord,
	RecordData.Forecast.Serialized
> {
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/forecasts') declare mmForecastsService: Service.ModelManager.Forecasts;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('billing') declare billingService: Service.Billing;
	@service('constants') declare constantsService: Service.Constants;
	@service('timeframe') declare timeframeService: Service.Timeframe;

	constructor(opts: { model: ForecastRecord }) {
		super(opts);
		next(() => {
			if (!this.model.timeframe && !this.model.endDate) {
				this.model.timeframe = {
					type: TimeframeType.Y,
					year: new Date().getFullYear(),
				};
			}

			if (!this.model.methodology) {
				this.model.methodology = {
					type: ForecastMethod.DATABOX,
					extra: null,
				};
			}
		});
	}

	// ------- props from /to ED record ------

	get id(): string | null {
		return this.model.id;
	}

	get blockId(): number {
		return this.model.blockId;
	}

	get timeframe(): Timeframe | null {
		return this.getBuffered('timeframe');
	}

	set timeframe(value: Timeframe | null) {
		this.setBuffered('timeframe', value);
	}

	get endDate(): string | null {
		return this.getBuffered('endDate');
	}

	set endDate(value: string | null) {
		this.setBuffered('endDate', value);
	}

	get metricSetupKey(): string {
		return this.getBuffered('metricSetupKey');
	}

	set metricSetupKey(value: string) {
		this.setBuffered('metricSetupKey', value);
	}

	get metricSetupSpaceAccessId(): number | null {
		return this.getBuffered('metricSetupSpaceAccessId');
	}

	set metricSetupSpaceAccessId(value: number | null) {
		this.setBuffered('metricSetupSpaceAccessId', value);
	}

	get metricSetupDimensions(): string[] {
		return this.model.metricSetupDimensions;
	}

	get metricSetupFilters(): DSSetup.MetricSetupFiltersData | null {
		return this.model.metricSetupFilters;
	}

	get created(): number {
		return this.model.created;
	}

	get createdBy(): number {
		return this.model.createdBy;
	}

	get order(): number | null {
		return this.getBuffered('order');
	}

	set order(value: number | null) {
		this.setBuffered('order', value);
	}

	get tags(): number[] {
		return this.getBuffered('tags');
	}

	set tags(value: number[]) {
		this.setBuffered('tags', value);
	}

	get methodology(): ForecastMethodology {
		return this.getBuffered('methodology');
	}

	set methodology(value: ForecastMethodology) {
		this.setBuffered('methodology', value);
	}

	get granulation(): TimeUnit {
		return this.getBuffered('granulation');
	}

	set granulation(value: TimeUnit) {
		this.setBuffered('granulation', value);
	}

	get seasonality(): TimeUnit[] {
		return this.getBuffered('seasonality');
	}

	set seasonality(value: TimeUnit[]) {
		this.setBuffered('seasonality', value);
	}

	get holidaysCountry(): string | null {
		return this.getBuffered('holidaysCountry');
	}

	set holidaysCountry(value: string | null) {
		this.setBuffered('holidaysCountry', value);
	}

	get comparisonPeriod(): ForecastComparisonPeriod {
		return this.getBuffered('comparisonPeriod');
	}

	set comparisonPeriod(value: ForecastComparisonPeriod) {
		this.setBuffered('comparisonPeriod', value);
	}

	get title(): string | null {
		return this.getBuffered('title');
	}

	set title(value: string | null) {
		this.setBuffered('title', value);
	}

	get lastSnapshotId(): number | null {
		return this.getBuffered('lastSnapshotId');
	}

	set lastSnapshotId(value: number | null) {
		this.setBuffered('lastSnapshotId', value);
	}

	get statusObject(): APIData.Forecast.StatusObject | null {
		return this.getBuffered('statusObject');
	}

	set statusObject(value: APIData.Forecast.StatusObject | null) {
		this.setBuffered('statusObject', value);
	}

	get statusObjectType(): string | undefined {
		return this.statusObject?.type;
	}

	get hasStatusError(): boolean {
		if (!this.statusObjectType) {
			return false;
		}

		return [
			ForecastStatusType.PREVIEW_GENERATION_FAILED,
			ForecastStatusType.SNAPSHOT_GENERATION_FAILED,
		].includes(this.statusObjectType as ForecastStatusType);
	}

	get isGenerating(): boolean {
		return this.statusObjectType === ForecastStatusType.SNAPSHOT_GENERATING;
	}

	get statusObjectMessage(): string | undefined {
		return this.statusObject?.data?.message;
	}

	// REGRESSORS stuff
	// cached so raw regressors become instances once: state set on them (status, preview data) and their
	// temp ids stay the same across reads, until the regressors change
	@cached
	get regressors(): RegressorMetric[] {
		const regressorsData: RegressorMetricData[] = this.getBuffered('regressors') || [];
		return regressorsData.map((regressor) => {
			if (regressor instanceof RegressorMetric) {
				return regressor;
			}
			return new RegressorMetric({ ...regressor, forecastId: this.model.id || '' });
		});
	}

	set regressors(value: RegressorMetric[]) {
		this.setBuffered('regressors', value);
	}

	// ------- other props ------

	get isMine() {
		return this.createdBy === this.currentUserService.numericId;
	}

	get canCurrentUserEdit() {
		return (
			(this.isMine || this.currentUserService.isAdmin || this.currentUserService.isEditor) &&
			this.billingService.forecastsEnabled &&
			!this.currentUserService.isDemo
		);
	}

	get attributeNames(): string[] {
		return this.metricSetup.attributeNamesFromBasicFilters;
	}

	get attributeName(): string {
		return this.metricSetup.firstAttributeNameFromBasicFilters;
	}

	get unitName(): string | null {
		return this.metricSetup.unitName;
	}

	@cached
	get metricSetup(): MetricSetup {
		return new MetricSetup({
			key: this.metricSetupKey,
			spaceAccessId: this.metricSetupSpaceAccessId,
			dimensions: this.metricSetupDimensions,
			filters: this.metricSetupFilters,
		});
	}

	get source() {
		return this.metricSetup?.source || null;
	}

	get displayTitle() {
		return this.title || this.metricSetup?.selectedMetricTitle || 'Unknown';
	}

	// ------- Permission and metric name helpers ------

	get isMissingMetric(): boolean {
		if (this.mmMetricsService.isMetricsRequested) {
			return !!this.metricSetupKey && !this.metricSetup.metric;
		}
		return false;
	}

	get hasNoMetricPermission(): boolean {
		return this.isMissingMetric && !this.currentUserService.isAdmin;
	}

	get metricNameFromData(): string | null {
		const historicalDataSelect = this.historicalDataSelect;
		return historicalDataSelect?.dataRoot?.metricName || null;
	}

	get displayMetricName(): string {
		return this.metricSetup?.selectedMetricTitle || this.metricNameFromData || 'Unknown metric';
	}

	// ------- End of permission and metric name helpers ------

	@cached
	get tagsObjs() {
		return this.mmTagsService.savedRecords.filter((tag) => {
			return this.tags.includes(+tag.id!);
		});
	}

	get tagsText(): string {
		return this.tagsObjs.map((item) => item.name).join(', ') || '';
	}

	get seasonalityTexts() {
		return this.seasonality.map((s) => DateRange.getTimeUnitText(s, true, true));
	}

	get holidayText() {
		const holidaysCountry = this.holidaysCountry;
		if (holidaysCountry) {
			const holidaysCountryLabel =
				this.constantsService.forecastHolidaysCountries.find(
					(i) => i.value === holidaysCountry,
				)?.label || '';
			return holidaysCountryLabel;
		}

		return '';
	}

	trackEvent(event: TrackingEvent) {
		const trackingData = this.mmForecastsService.getTrackingData(
			this.metricSetup,
			undefined, // No date range needed anymore
			this.seasonality,
			this.holidaysCountry,
		);
		if (trackingData) {
			this.mmForecastsService.trackEvent(event, trackingData);
		}
	}

	// ----------- DATA stuff -----------

	@tracked _block: Block | null = null;
	@tracked tableBlock: Block | null = null;

	get block() {
		if (!this._block && this.blockId) {
			const block = this.mmBlocksService.findModel(String(this.blockId));
			if (block) {
				this._block = block;
				this.initializeVisualization();
			} else {
				next(this, this.createAndSetFakeBlock);
			}
		}
		return this._block;
	}

	@cached
	get visualization() {
		return this.block?.blockSetup.visualization as VisualizationLineChart;
	}

	get sample(): SampleRecord | null {
		return this.block?.sample || null;
	}

	@cached
	get dataSelects(): DataSelect[] {
		return this.block?.dataSelects || [];
	}

	@cached
	get isSyncing() {
		return this.dataSelects.some((ds) => {
			return ds.statusObjects.some((so) => so.isSync);
		});
	}

	@cached
	get lastSync() {
		return this.block?.lastSync;
	}

	get lastSyncText() {
		const lastSync = this.lastSync;
		if (lastSync) {
			return getRelativeTime(lastSync);
		}
		return '';
	}

	get historicalDataSelect() {
		const dataSelects = this.tableBlock?.dataSelects;
		if (!dataSelects) {
			return null;
		}
		return dataSelects.find((ds) => ds.isMain) || null;
	}

	get forecastDataSelect() {
		const dataSelects = this.tableBlock?.dataSelects;
		if (!dataSelects) {
			return null;
		}
		return dataSelects.find((ds) => ds.isForecast) || null;
	}

	get forecastWholeRangeDataSelect() {
		const dataSelects = this.tableBlock?.dataSelects;
		if (!dataSelects) {
			return null;
		}
		return dataSelects.find((ds) => ds.type === DataSelectType.FORECAST_WHOLE_RANGE) || null;
	}

	get compareDataSelect() {
		const dataSelects = this.tableBlock?.dataSelects;
		if (!dataSelects) {
			return null;
		}
		return dataSelects.find((ds) => ds.isCompare && !ds.isOneOfForecasts) || null;
	}

	get currentValueDataSelect() {
		const dataSelects = this.tableBlock?.dataSelects;
		if (!dataSelects) {
			return null;
		}
		// find the MAIN type data select that has updateFrom set (which updates from the main historical data select)
		return dataSelects.find((ds) => ds.isMain && !!ds.updateFrom) || null;
	}

	getTimeframeDisplayString(): string {
		if (this.endDate) {
			return 'Total';
		} else if (this.timeframe) {
			return this.getTimeframeLabel();
		} else {
			return `${new Date().getFullYear()}`;
		}
	}

	getTimeframeLabel(yearOffset = 0): string {
		if (!this.timeframe) {
			return `${new Date().getFullYear() + yearOffset}`;
		}
		const year = this.timeframe.year + yearOffset;
		switch (this.timeframe.type) {
			case TimeframeType.Y:
				return `${year}`;
			case TimeframeType.Q1:
				return `Q1 ${year}`;
			case TimeframeType.Q2:
				return `Q2 ${year}`;
			case TimeframeType.Q3:
				return `Q3 ${year}`;
			case TimeframeType.Q4:
				return `Q4 ${year}`;
			case TimeframeType.H1:
				return `H1 ${year}`;
			case TimeframeType.H2:
				return `H2 ${year}`;
			default:
				return `${year}`;
		}
	}

	generateDefaultTitle(): string {
		const metricPart = this.metricSetup?.selectedMetricTitle || 'Unknown metric';

		let timeframePart: string;
		if (this.endDate) {
			// transform YYYY_MM format to "Month YYYY"
			const formattedDate = moment(this.endDate, 'YYYY_MM').format('MMMM YYYY');
			timeframePart = `end of ${formattedDate}`;
		} else {
			timeframePart = this.getTimeframeDisplayString();
		}

		return `${metricPart} scenario for ${timeframePart}`;
	}

	updateTimeframeToCurrent(): void {
		const timeframe = this.timeframe;
		const endDate = this.endDate;

		if (timeframe) {
			// year timeframe - type 0 - use current year
			if (timeframe.type === TimeframeType.Y) {
				this.timeframe = {
					type: TimeframeType.Y,
					year: new Date().getFullYear(),
				};
			}
			// quarter timeframe - type 1,2,3,4 - use current quarter
			else if (
				[TimeframeType.Q1, TimeframeType.Q2, TimeframeType.Q3, TimeframeType.Q4].includes(
					timeframe.type,
				)
			) {
				const currentQuarter = this.timeframeService.getCurrentQuarter();
				this.timeframe = {
					type: currentQuarter,
					year: new Date().getFullYear(),
				};
			}
		} else if (endDate) {
			// if timeframe is null and endDate is set, use current month as endDate value
			const now = new Date();
			const currentMonth = now.getMonth() + 1; // 1-12
			const currentYear = now.getFullYear();
			this.endDate = `${currentYear}_${String(currentMonth).padStart(2, '0')}`;
		}
	}

	buildRegressorsPayload(): RecordData.RegressorMetric.Serialized[] {
		return this.regressors
			.filter((regressor) => {
				// for enabled LINEAR_CHANGE regressors, ensure changeValue is provided
				if (
					regressor.enabled &&
					regressor.methodology?.type === ForecastMethod.LINEAR_CHANGE
				) {
					const changeValue = regressor.methodology?.extra?.changeValue;
					return changeValue !== null && changeValue !== undefined;
				}

				return true;
			})
			.map((regressor) => {
				return {
					metricSetup: serializeMetricSetupData({
						key: regressor.metricSetupKey,
						spaceAccessId: regressor.metricSetupSpaceAccessId,
						dimensions: regressor.metricSetupDimensions,
						filters: regressor.metricSetupFilters,
					})!,
					methodology: regressor.methodology,
					correlation: regressor.correlation,
					enabled: regressor.enabled,
				};
			});
	}

	private createAndSetFakeBlock() {
		const blockId = this.blockId;
		if (blockId) {
			if (!this.mmBlocksService.findRecord(String(blockId))) {
				this._block = this.mmBlocksService.createNewModel({
					id: blockId,
					visualization: BlockVisualization.LINE_CHART,
				});
				this.initializeVisualization();
			}
		}
	}

	private initializeVisualization() {
		const visualization = this.visualization;
		visualization.forceDChartRendererType = BlockVisualizationSpecial.FORECAST_CHART_SMALL;
		this.visualization.checkToUpdateDataCache();
	}

	// ----------- Static stuff -----------

	static override modelName = ModelName.FORECAST; // store record type
}
